import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { ExamQuestion, ExamType, ExamWithQuestions, Sport } from '@proref/types';
import type { UpdateExamInput } from '@proref/schemas';
import { useAuth } from '../../contexts/auth-context';
import { PageHeader } from '../../components/shared/PageHeader';
import { Icon } from '../../components/shared/Icons';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Field } from '../../components/shared/Field';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Skeleton } from '../../components/shared/Skeleton';
import { Toggle } from '../../components/shared/Toggle';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';
import { ClipPickerDialog } from '../../components/exams/ClipPickerDialog';
import { QuestionPickerDialog } from '../../components/exams/QuestionPickerDialog';
import { ExamTabs, examsListPath } from '../../components/exams/ExamTabs';
import { AnswerRow } from '../../components/exams/AnswerRow';
import {
  useExamQuery,
  useUpdateExam,
  usePublishExam,
  useUnpublishExam,
  useAddExamRuleQuestions,
  useDeleteExamQuestion,
} from '../../hooks/use-exams';
import { useSportsQuery, useRulesQuery } from '../../hooks/use-sports';

function typeLabel(t: TFunction, type: ExamType): string {
  return t(`admin.exams.editor.typeLabel.${type}`);
}

// a. b. c. d. — option labels for the stacked answers (matches the design).
const OPTION_LETTERS = 'abcdefgh'.split('');

// A question STAGED locally in the editor (from the custom hand-written form) but not yet
// persisted. Adding stages instantly with no network; the whole batch is saved in one request when
// the admin presses "Save questions". Library picks bypass staging entirely now — the
// QuestionPickerDialog modal persists them directly, same as the clip picker.
interface StagedQuestion {
  tempId: string;
  rule_article_id: string;
  ruleLabel: string; // "Rule 5 · The goalkeeper" for the pending-row badge
  question_text: string | null;
  options: string[];
  // Every option marked correct. One entry is the ordinary single-answer question; several make
  // it a "select all that apply" question, which the runner grades with partial credit.
  correct_answers: string[];
}

// Local, collision-free key for a staged question (no persistence — just a React key + handle).
const newTempId = (): string => crypto.randomUUID();


// A proctored exam is auto-timed at a shared pool of 90s × question count.
const PROCTORED_SECONDS_PER_QUESTION = 90;

// "90s × 18 = 27 min" style readout for the Official auto-timer.
function autoTimeLabel(t: TFunction, questionCount: number): string {
  const total = questionCount * PROCTORED_SECONDS_PER_QUESTION;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s === 0
    ? t('admin.exams.editor.minutesLabel', { m })
    : t('admin.exams.editor.minutesSecondsLabel', { m, s });
}

// Editable exam meta — title, type + sport (only while there are 0 questions; the create modal is
// gone, so these live here now), the Official (proctored) toggle, and the time limit. Saves via
// PATCH /v1/exams/:id. When Official is on the timer is auto (90s × question count), so the manual
// field is replaced by a live readout.
function MetaCard({
  exam,
  questionCount,
  isSuper,
  sports,
}: {
  exam: ExamWithQuestions;
  questionCount: number;
  isSuper: boolean;
  sports: Sport[];
}) {
  const { t } = useTranslation();
  const update = useUpdateExam();
  const [title, setTitle] = useState(exam.title);
  const [type, setType] = useState<ExamType>(exam.type);
  const [sportId, setSportId] = useState(exam.sport_id);
  const [official, setOfficial] = useState(exam.is_proctored);
  // Blank = keep the current password (if any). A value sets/replaces it. Never pre-filled (the
  // hash never leaves the server) — we only know whether one exists via exam.has_password.
  const [password, setPassword] = useState('');
  const [mins, setMins] = useState(exam.time_limit_sec === null ? '' : String(Math.round(exam.time_limit_sec / 60)));

  // Type + sport are locked once questions exist (changing them would orphan the questions).
  const locked = questionCount > 0;
  const isRuleTest = type === 'rule_test';
  const isClipExam = type === 'video_clip_exam';
  // Official is a rule-test-only flag — a clip exam can't be proctored, so treat it as off there.
  const officialOn = official && isRuleTest;

  async function save() {
    try {
      const input: UpdateExamInput = { title: title.trim(), is_proctored: officialOn };
      // Only send structural edits when they're both allowed (no questions) and actually changed.
      if (!locked && type !== exam.type) input.type = type;
      if (!locked && isSuper && sportId !== exam.sport_id) input.sport_id = sportId;
      // Proctored derives its own limit server-side; only send a manual limit when not official.
      if (!officialOn) input.time_limit_sec = mins.trim() === '' ? null : Math.max(1, Math.round(Number(mins))) * 60;
      // Only send the password when Official and a new value was typed (blank keeps the current one).
      if (officialOn && password.trim() !== '') input.password = password;
      await update.mutateAsync({ id: exam.id, input });
      setPassword('');
      notifySuccess(t('admin.exams.editor.meta.savedToast'));
    } catch (err) {
      notifyError(err, t('admin.exams.editor.meta.saveErrorToast'));
    }
  }

  return (
    <div className="rounded border border-line bg-surface p-4">
      <div className="grid gap-3.5 sm:grid-cols-2">
        <Field label={t('admin.exams.editor.meta.titleLabel')} htmlFor="edit-title">
          <Input id="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>

        <Field
          label={locked ? t('admin.exams.editor.meta.typeLockedLabel') : t('admin.exams.editor.meta.typeLabel')}
          htmlFor="edit-type"
        >
          <Select
            id="edit-type"
            value={type}
            disabled={locked}
            onChange={(e) => setType(e.target.value as ExamType)}
          >
            <option value="rule_test">{t('admin.exams.editor.meta.typeRuleTestOption')}</option>
            <option value="video_clip_exam">
              {isSuper
                ? t('admin.exams.editor.meta.typeClipExamOptionSuper')
                : t('admin.exams.editor.meta.typeClipExamOption')}
            </option>
          </Select>
        </Field>

        {/* super_admin can retarget a platform exam's sport while it's empty; a federation_admin is
            pinned to its own sport (no picker). */}
        {isSuper && (
          <Field
            label={locked ? t('admin.exams.editor.meta.sportLockedLabel') : t('admin.exams.editor.meta.sportLabel')}
            htmlFor="edit-sport"
          >
            <Select id="edit-sport" value={sportId} disabled={locked} onChange={(e) => setSportId(e.target.value)}>
              {sports.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

      </div>

      {/* Timing — full-width below the title/type row. Clip exams are untimed; rule tests get the
          Official toggle + a timer. */}
      <div className="mt-3.5">
        {isClipExam ? (
          <Field label={t('admin.exams.editor.meta.timeLimitLabel')}>
            <p className="flex h-9 items-center rounded-btn border border-dashed border-line-2 bg-bg-2 px-3 text-[12.5px] text-muted">
              {t('admin.exams.editor.meta.untimedNote')}
            </p>
          </Field>
        ) : officialOn ? (
          <Field label={t('admin.exams.editor.meta.timeLimitOfficialLabel')}>
            <div className="flex h-9 items-center gap-2 rounded-btn border border-line bg-bg-2 px-3">
              <Icon.calendar className="h-3.5 w-3.5 shrink-0 text-muted" />
              <span className="font-mono text-[12.5px] text-ink">
                {t('admin.exams.editor.autoTimerFormula', {
                  count: questionCount,
                  time: autoTimeLabel(t, questionCount),
                })}
              </span>
              <span className="ml-auto text-[11px] text-muted">{t('admin.exams.editor.meta.autoCalculatedNote')}</span>
            </div>
          </Field>
        ) : (
          <Field label={t('admin.exams.editor.meta.timeLimitManualLabel')} htmlFor="edit-limit">
            <Input id="edit-limit" type="number" min={1} value={mins} onChange={(e) => setMins(e.target.value)} />
          </Field>
        )}
      </div>

      {/* Official (proctored) toggle — a rule-test-only flag both federation_admins and super_admins
          can set. It auto-times the exam (90s × questions) and gates referees behind a password. */}
      {isRuleTest && (
        <div
          className={
            'mt-3 rounded-btn border-[1.5px] px-3.5 py-3 transition-colors ' +
            (official ? 'border-accent bg-accent/[0.07]' : 'border-line bg-bg-2')
          }
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Toggle checked={official} onChange={setOfficial} label={t('admin.exams.editor.meta.officialToggleLabel')} />
            </div>
            <div className="text-[12.5px] text-ink-2">
              <div className="font-semibold text-ink">{t('admin.exams.editor.meta.officialToggleLabel')}</div>
              <p className="mt-0.5">
                {t('admin.exams.editor.meta.officialDescription')}
              </p>
            </div>
          </div>

          {official && (
            <div className="mt-3 border-t border-accent/25 pt-3">
              <Field
                label={
                  exam.has_password
                    ? t('admin.exams.editor.meta.passwordSetLabel')
                    : t('admin.exams.editor.meta.passwordRequiredLabel')
                }
                htmlFor="edit-pw"
              >
                <Input
                  id="edit-pw"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={
                    exam.has_password
                      ? t('admin.exams.editor.meta.passwordPlaceholderUnchanged')
                      : t('admin.exams.editor.meta.passwordPlaceholderSet')
                  }
                  autoComplete="new-password"
                />
              </Field>
              {/* Publishing an Official exam without a password is blocked server-side — warn early. */}
              {!exam.has_password && password.trim() === '' && (
                <p className="mt-1.5 text-[11.5px] text-danger">
                  {t('admin.exams.editor.meta.passwordWarning')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <Button variant="default" size="sm" onClick={save} disabled={update.isPending}>
          {update.isPending ? <Spinner className="w-4 h-4" /> : t('admin.exams.editor.meta.saveDetailsButton')}
        </Button>
      </div>
    </div>
  );
}

// Add a RULE_TEST question: pick a rule article, write the prompt + 2–8 options, mark the correct
// one. Submitting STAGES it locally (no network) — the editor persists the batch on Save.
function AddRuleQuestion({
  sportSlug,
  onStage,
}: {
  sportSlug: string | undefined;
  onStage: (q: StagedQuestion) => void;
}) {
  const { t } = useTranslation();
  const rulesQuery = useRulesQuery(sportSlug);
  const [ruleId, setRuleId] = useState('');
  const [text, setText] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  // Indices of the options marked correct — a Set rather than a single index, since a question
  // may have several right answers.
  const [correct, setCorrect] = useState<Set<number>>(new Set([0]));

  function toggleCorrect(i: number) {
    setCorrect((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  function setOption(i: number, v: string) {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));
  }

  function submit() {
    const cleaned = options.map((o) => o.trim()).filter((o) => o.length > 0);
    if (!ruleId) return notifyError(t('admin.exams.editor.addRuleQuestion.pickRuleError'));
    if (cleaned.length < 2) return notifyError(t('admin.exams.editor.addRuleQuestion.twoOptionsError'));
    // Map the ticked indices back to their text, dropping any that point at a blank option the
    // admin never filled in.
    const correctValues = [...correct]
      .map((i) => options[i]?.trim())
      .filter((v): v is string => Boolean(v) && cleaned.includes(v!));
    if (correctValues.length === 0) return notifyError(t('admin.exams.editor.addRuleQuestion.markCorrectError'));
    const rule = (rulesQuery.data ?? []).find((r) => r.id === ruleId);
    // Stage it — the details Save button (or the questions Save bar) persists the whole batch.
    onStage({
      tempId: newTempId(),
      rule_article_id: ruleId,
      ruleLabel: rule ? `${t('common.ruleLabel', { number: rule.article_number })} · ${rule.title}` : '',
      question_text: text.trim() || null,
      options: cleaned,
      correct_answers: correctValues,
    });
    setText('');
    setOptions(['', '']);
    setCorrect(new Set([0]));
    setRuleId('');
  }

  return (
    <div className="flex flex-col gap-3">
      <Field label={t('admin.exams.editor.addRuleQuestion.ruleArticleLabel')} htmlFor="q-rule">
        <Select id="q-rule" value={ruleId} onChange={(e) => setRuleId(e.target.value)}>
          <option value="" disabled>
            {rulesQuery.isLoading
              ? t('admin.exams.editor.addRuleQuestion.loadingRules')
              : t('admin.exams.editor.addRuleQuestion.selectRulePlaceholder')}
          </option>
          {(rulesQuery.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.article_number} · {r.title}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('admin.exams.editor.addRuleQuestion.questionLabel')} htmlFor="q-text">
        <Input
          id="q-text"
          placeholder={t('admin.exams.editor.addRuleQuestion.questionPlaceholder')}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-medium text-muted">
          {t('admin.exams.editor.addRuleQuestion.optionsHint')}
        </span>
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            {/* Checkbox, not radio: a question may have more than one right answer. */}
            <input
              type="checkbox"
              checked={correct.has(i)}
              onChange={() => toggleCorrect(i)}
              aria-label={t('admin.exams.editor.addRuleQuestion.markOptionCorrectAria', { n: i + 1 })}
            />
            <Input
              value={opt}
              placeholder={t('admin.exams.editor.addRuleQuestion.optionPlaceholder', { n: i + 1 })}
              onChange={(e) => setOption(i, e.target.value)}
            />
            {options.length > 2 && (
              <Button
                variant="ghost"
                size="sm"
                icon
                aria-label={t('admin.exams.editor.addRuleQuestion.removeOptionAria', { n: i + 1 })}
                onClick={() => {
                  setOptions((prev) => prev.filter((_, idx) => idx !== i));
                  // Removing an option shifts every later index down by one, so the ticked set
                  // has to be re-mapped or the marks would silently slide onto other options.
                  setCorrect((prev) => {
                    const next = new Set<number>();
                    for (const idx of prev) {
                      if (idx < i) next.add(idx);
                      else if (idx > i) next.add(idx - 1);
                    }
                    return next;
                  });
                }}
              >
                <Icon.close className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
        {options.length < 8 && (
          <button
            type="button"
            className="self-start text-[12px] text-accent hover:underline"
            onClick={() => setOptions((prev) => [...prev, ''])}
          >
            {t('admin.exams.editor.addRuleQuestion.addOption')}
          </button>
        )}
      </div>
      <div className="flex justify-end">
        <Button variant="accent" size="sm" onClick={submit}>
          {t('admin.exams.editor.addRuleQuestion.addQuestionButton')}
        </Button>
      </div>
    </div>
  );
}

// Clip-exam authoring: opens the ClipPickerDialog — a searchable library grid that already
// handles picking one clip just as well as picking several, so there's nothing a single-clip
// quick-add form would offer that the modal doesn't (a prior "or add one at a time" dropdown was
// dropped as redundant once the modal existed).
function ClipQuestionAuthoring({
  examId,
  federationId,
  sportId,
  official,
  existingClipIds,
}: {
  examId: string;
  federationId: string;
  sportId: string;
  // An Official (platform) exam draws from OFFICIAL clips (federation_id NULL).
  official: boolean;
  existingClipIds: string[];
}) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  // No border/background of its own — the caller already wraps this in the standard
  // `border border-line bg-surface` section card (same as the rule test's Question library), so
  // adding one here would double up into a card-inside-a-card.
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-[12.5px] text-muted">{t('admin.exams.editor.clipAuthoring.hint')}</p>
      <Button variant="accent" size="sm" onClick={() => setPickerOpen(true)}>
        <Icon.plus className="h-3.5 w-3.5" /> {t('admin.exams.editor.clipAuthoring.selectClips')}
      </Button>

      <ClipPickerDialog
        examId={examId}
        federationId={federationId}
        sportId={sportId}
        official={official}
        existingClipIds={existingClipIds}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
      />
    </div>
  );
}

// Rule-test authoring: opens the QuestionPickerDialog — a searchable, filterable checklist over
// the reusable question library. Picking and pressing "Add" persists the whole batch in one call,
// the same "modal → pick from the bank → done" shape as the clip exam's picker.
function RuleQuestionLibraryAuthoring({
  examId,
  sportId,
  sportSlug,
  existingQuestionTexts,
}: {
  examId: string;
  sportId: string;
  sportSlug: string | undefined;
  existingQuestionTexts: Set<string>;
}) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  // No border/background of its own — the caller already wraps this in the standard
  // `border border-line bg-surface` section card (same as the clip exam's "Add a question").
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-[12.5px] text-muted">{t('admin.exams.editor.ruleLibraryAuthoring.hint')}</p>
      <Button variant="accent" size="sm" onClick={() => setPickerOpen(true)}>
        <Icon.plus className="h-3.5 w-3.5" /> {t('admin.exams.editor.ruleLibraryAuthoring.selectQuestions')}
      </Button>

      <QuestionPickerDialog
        examId={examId}
        sportId={sportId}
        sportSlug={sportSlug}
        existingQuestionTexts={existingQuestionTexts}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
      />
    </div>
  );
}

// One authored question in the list. Rule questions show their options with the correct one
// tinted; clip questions show the referenced clip id. Delete only (edit = delete + re-add for now).
function QuestionRow({ examId, question, index }: { examId: string; question: ExamQuestion; index: number }) {
  const { t } = useTranslation();
  const del = useDeleteExamQuestion(examId);

  async function remove() {
    try {
      await del.mutateAsync(question.id);
      notifySuccess(t('admin.exams.editor.questionRow.removedToast'));
    } catch (err) {
      notifyError(err, t('admin.exams.editor.questionRow.removeErrorToast'));
    }
  }

  return (
    <div className="rounded border border-line bg-surface p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {/* A clip question is identified by its own video, not a typed prompt — the thumbnail
              is what makes "which clip is this" scannable at a glance in a list of otherwise
              identical "reuses the clip's decision" rows. */}
          {question.clip_id && (
            <div className="h-12 w-20 flex-none overflow-hidden rounded-sm bg-bg-2">
              {question.clip?.poster_url ? (
                <img src={question.clip.poster_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="grid h-full w-full place-items-center text-[9px] text-muted">{t('common.noPoster')}</div>
              )}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[12px] font-medium text-muted">{t('admin.exams.editor.questionRow.questionLabel', { n: index + 1 })}</div>
            {question.clip_id ? (
              <>
                <div className="mt-1 truncate text-[13px] font-medium text-ink">
                  {/* clip is null when the referenced clip was since deleted — name absent rather
                      than dangling on a title that no longer resolves to anything. */}
                  {question.clip?.title ?? t('admin.exams.editor.questionRow.clipRemoved')}
                </div>
                <div className="mt-0.5 text-[11.5px] text-muted">{t('admin.exams.editor.questionRow.clipQuestionNote')}</div>
              </>
            ) : (
              <>
                <div className="mt-1 text-[13px] text-ink">{question.question_text || t('admin.exams.editor.questionRow.defaultPrompt')}</div>
                {/* Answers listed one under the other, labelled a/b/c/d; the correct one is boxed + badged. */}
                <ol className="mt-2 flex flex-col gap-1.5">
                  {question.options.map((opt, i) => (
                    <AnswerRow
                      key={opt}
                      letter={OPTION_LETTERS[i]!}
                      text={opt}
                      correct={(question.correct_answers ?? []).includes(opt)}
                    />
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon
          aria-label={t('admin.exams.editor.questionRow.removeAria', { n: index + 1 })}
          onClick={remove}
          disabled={del.isPending}
        >
          <Icon.trash className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// A question STAGED but not yet saved — shown in the Questions list with an "Unsaved" tag and a
// local remove (×). No network: removing just drops it from local state, adding never touched it.
function StagedRow({
  question,
  index,
  onRemove,
}: {
  question: StagedQuestion;
  index: number;
  onRemove: (tempId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded border border-dashed border-accent/50 bg-accent/[0.04] p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12px] font-medium text-muted">{t('admin.exams.editor.questionRow.questionLabel', { n: index + 1 })}</span>
            <span className="inline-flex items-center rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
              {t('admin.exams.editor.unsavedBadge')}
            </span>
            {question.ruleLabel && (
              <span className="inline-flex items-center rounded-full border border-line-2 bg-surface-2 px-2 py-0.5 font-mono text-[10.5px] text-ink-2">
                {question.ruleLabel}
              </span>
            )}
          </div>
          <div className="mt-1 text-[13px] text-ink">{question.question_text || t('admin.exams.editor.questionRow.defaultPrompt')}</div>
          <ol className="mt-2 flex flex-col gap-1.5">
            {question.options.map((opt, i) => (
              <AnswerRow key={opt} letter={OPTION_LETTERS[i]!} text={opt} correct={question.correct_answers.includes(opt)} />
            ))}
          </ol>
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon
          aria-label={t('admin.exams.editor.stagedRow.removeAria', { n: index + 1 })}
          onClick={() => onRemove(question.tempId)}
        >
          <Icon.close className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function ExamEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role } = useAuth();
  const isSuper = role === 'super_admin';
  const { data: exam, isLoading, isError, error } = useExamQuery(id);
  const publish = usePublishExam();
  const unpublish = useUnpublishExam();
  const sportsQuery = useSportsQuery();
  // Questions staged locally (from the library or the custom form) but not yet persisted. Adding is
  // instant + offline; the whole batch is saved in ONE request on "Save questions".
  const [staged, setStaged] = useState<StagedQuestion[]>([]);
  const saveQuestions = useAddExamRuleQuestions(id ?? '');

  // The sport's slug drives the rule-article picker (rules are keyed by slug).
  const sportSlug = useMemo(
    () => (sportsQuery.data ?? []).find((s) => s.id === exam?.sport_id)?.slug,
    [sportsQuery.data, exam?.sport_id],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (isError || !exam) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.exams.editor.couldNotLoad'))}
      </div>
    );
  }

  const isPublished = exam.published_at !== null;
  const togglePending = publish.isPending || unpublish.isPending;
  // Capture the id so the async closure keeps its narrowing (exam is defined past the guard).
  const examId = exam.id;
  // Effective count = persisted + staged, so the header, the auto-timer preview and the type/sport
  // lock all reflect what the exam will be once the pending batch is saved.
  const savedCount = exam.questions.length;
  const effectiveCount = savedCount + staged.length;

  function stageQuestion(q: StagedQuestion) {
    setStaged((prev) => [...prev, q]);
  }
  function unstage(tempId: string) {
    setStaged((prev) => prev.filter((s) => s.tempId !== tempId));
  }

  // Persist every staged question in ONE atomic request, then clear the staging area (the exam
  // refetch brings them back as saved rows).
  async function saveStagedQuestions() {
    if (staged.length === 0) return;
    try {
      await saveQuestions.mutateAsync({
        questions: staged.map((s) => ({
          rule_article_id: s.rule_article_id,
          question_text: s.question_text ?? undefined,
          options: s.options,
          correct_answers: s.correct_answers,
        })),
      });
      setStaged([]);
      notifySuccess(t('admin.exams.editor.questionsSavedToast', { count: staged.length }));
    } catch (err) {
      notifyError(err, t('admin.exams.editor.saveQuestionsErrorToast'));
    }
  }

  async function togglePublish() {
    // Don't publish with unsaved questions on the table — they'd be silently left out.
    if (!isPublished && staged.length > 0) {
      notifyWarning(t('admin.exams.editor.publishUnsavedWarning'));
      return;
    }
    try {
      if (isPublished) {
        await unpublish.mutateAsync(examId);
        notifySuccess(t('admin.exams.editor.examUnpublishedToast'));
      } else {
        await publish.mutateAsync(examId);
        notifySuccess(t('admin.exams.editor.examPublishedToast'));
      }
    } catch (err) {
      notifyError(
        err,
        isPublished ? t('admin.exams.publishToggle.unpublishErrorToast') : t('admin.exams.publishToggle.publishErrorToast'),
      );
    }
  }

  return (
    <>
      <div className="mb-2">
        <Link
          to={examsListPath(exam.type)}
          className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-ink"
        >
          <Icon.chevronLeft className="h-3.5 w-3.5" /> {t('admin.exams.editor.allExams')}
        </Link>
      </div>
      <PageHeader
        title={exam.title}
        sub={[
          typeLabel(t, exam.type),
          ...(exam.is_proctored ? [t('admin.exams.editor.officialTag')] : []),
          t('admin.exams.editor.questionsCount', { count: effectiveCount }),
        ].join(' · ')}
        actions={
          <div className="flex items-center gap-2">
            <span
              className={
                'inline-flex h-[24px] items-center rounded-full px-3 text-[11px] font-medium ' +
                (isPublished ? 'bg-accent/15 text-accent' : 'bg-surface-2 text-muted')
              }
            >
              {isPublished ? t('admin.exams.list.published') : t('admin.exams.list.draft')}
            </span>
            <Button variant={isPublished ? 'default' : 'accent'} onClick={togglePublish} disabled={togglePending}>
              {togglePending ? (
                <Spinner className="w-4 h-4" />
              ) : isPublished ? (
                t('admin.exams.publishToggle.unpublish')
              ) : (
                t('admin.exams.publishToggle.publish')
              )}
            </Button>
          </div>
        }
      />

      <ExamTabs examId={exam.id} />

      <div className="flex flex-col gap-6">
        <MetaCard
          exam={exam}
          questionCount={effectiveCount}
          isSuper={isSuper}
          sports={sportsQuery.data ?? []}
        />

        <section>
          <h2 className="mb-2.5 text-[13px] font-semibold uppercase tracking-[0.05em] text-muted">
            {t('admin.exams.editor.questionsSectionTitle', { count: effectiveCount })}
          </h2>
          {effectiveCount === 0 ? (
            <div className="rounded border border-dashed border-line-2 bg-bg-2 px-6 py-8 text-center text-sm text-muted">
              {exam.type === 'rule_test'
                ? t('admin.exams.editor.noQuestionsRuleTest')
                : t('admin.exams.editor.noQuestionsClipExam')}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {/* Persisted questions first, then the locally-staged (unsaved) ones. */}
              {exam.questions.map((q, i) => (
                <QuestionRow key={q.id} examId={exam.id} question={q} index={i} />
              ))}
              {staged.map((q, i) => (
                <StagedRow key={q.tempId} question={q} index={savedCount + i} onRemove={unstage} />
              ))}
            </div>
          )}

          {/* Save bar — only while there are unsaved questions. Persists the whole batch at once. */}
          {staged.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded border border-accent/40 bg-accent/[0.06] px-4 py-3">
              <p className="text-[12.5px] text-ink-2">
                <span className="font-medium text-ink">
                  {t('admin.exams.editor.unsavedBar', { count: staged.length })}
                </span>{' '}
                {t('admin.exams.editor.unsavedBarSuffix')}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStaged([])} disabled={saveQuestions.isPending}>
                  {t('admin.exams.editor.discard')}
                </Button>
                <Button variant="accent" size="sm" onClick={saveStagedQuestions} disabled={saveQuestions.isPending}>
                  {saveQuestions.isPending ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    t('admin.exams.editor.saveQuestionsButton', { count: staged.length })
                  )}
                </Button>
              </div>
            </div>
          )}
        </section>

        {exam.type === 'rule_test' ? (
          <>
            {/* Primary authoring path: pick pre-authored questions from the reusable library via
                the picker modal — same "open a modal, tick the ones you want, done" shape as the
                clip exam's "Add a question" section below. */}
            <section>
              <h2 className="mb-2.5 text-[13px] font-semibold uppercase tracking-[0.05em] text-muted">{t('admin.exams.editor.questionLibraryTitle')}</h2>
              <div className="rounded border border-line bg-surface p-4">
                <RuleQuestionLibraryAuthoring
                  examId={exam.id}
                  sportId={exam.sport_id}
                  sportSlug={sportSlug}
                  existingQuestionTexts={
                    new Set(
                      exam.questions
                        .map((q) => q.question_text)
                        .filter((t): t is string => t !== null),
                    )
                  }
                />
              </div>
            </section>

            {/* Secondary: hand-write a one-off question not in the library. */}
            <section>
              <h2 className="mb-2.5 text-[13px] font-semibold uppercase tracking-[0.05em] text-muted">
                {t('admin.exams.editor.orCustomTitle')}
              </h2>
              <div className="rounded border border-line bg-surface p-4">
                <AddRuleQuestion sportSlug={sportSlug} onStage={stageQuestion} />
              </div>
            </section>
          </>
        ) : (
          <section>
            <h2 className="mb-2.5 text-[13px] font-semibold uppercase tracking-[0.05em] text-muted">{t('admin.exams.editor.addQuestionTitle')}</h2>
            <div className="rounded border border-line bg-surface p-4">
              {/* A clip exam always has a federation (platform exams are rule tests) — `?? ''` just
                  satisfies the nullable type. existingClipIds pre-checks clips already on the exam
                  in the picker so they can't be double-added. */}
              <ClipQuestionAuthoring
                examId={exam.id}
                federationId={exam.federation_id ?? ''}
                sportId={exam.sport_id}
                official={exam.federation_id === null}
                existingClipIds={exam.questions
                  .map((q) => q.clip_id)
                  .filter((c): c is string => c !== null)}
              />
            </div>
          </section>
        )}

        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(examsListPath(exam.type))}>
            {t('admin.exams.editor.doneButton')}
          </Button>
        </div>
      </div>
    </>
  );
}
